import awsconfig from '@/amplifyconfiguration.json';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Amplify } from 'aws-amplify';
import { ExaminationMenuForm, PatientInformationForm, ReservationDateTimeForm } from '../routes';
import Layout from './Layout';
import { ReservationDone } from './ReservationDone';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getPatient } from '@/graphql/queries';
import { Grid, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ReservationUrls } from '../constants/urls';
import { FacilityResponseType } from '../types/FacilityResponseType';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executeGet } from '@/client/rest';
import { GetPatientQuery } from '@/API';
import { executeQuery } from '@/client/graphql';

Amplify.configure(awsconfig);

interface ReservationFormProps {
  isLogin?: boolean; // publicRouteかどうかで判断
}
export function ReservationForm({ isLogin }: ReservationFormProps) {
  const locate = useLocation().state;
  const [registeredStatus, setRegisteredStatus] = useState('Reserve');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('fid');

  // LINEからカレンダーページへ直接遷移した場合はメニューIDをクエリパラメータから取得する。
  const outPatientIdFromQs = searchParams.get('oid');
  const outPatientId = outPatientIdFromQs ?? locate?.outpatientid ?? null;

  useEffect(() => {
    const fetchRegisteredData = async (userSub: string) => {
      try {
        if (userSub) {
          const result = await executeQuery<GetPatientQuery>(getPatient, { id: userSub }, 'userPool');

          const patientData = result.getPatient;
          if (patientData) {
            setRegisteredStatus('Revisit');
          } else {
            setRegisteredStatus('Reserve');
          }
          // fidから施設名を取得し,セットする
          handleSetFacilityName();
        }
      } catch (error) {
        if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
          Sentry.captureException(error);
        }

        console.error('Error fetching patient data:', error);
        setRegisteredStatus('Reserve');
      }
    };

    // 非ログイン状態・ログイン状態両方で通る
    const fetchCognitoIdAndRegisteredData = async () => {
      const session = await fetchAuthSession();
      const userSub = session.userSub;
      if (!userSub) {
        handleSetFacilityName();
        if (facilityId) {
          // ログイン時に施設名を表示するため、施設IDをセッションストレージに保存。
          // 同一タブ内のみ有効なデータとして保存。
          sessionStorage.setItem('fid', facilityId);
        }
        return;
      }
      // すでにpatientテーブルにデータが登録済みかどうかを確認する
      await fetchRegisteredData(userSub).then(() => {
        currentState.registeredStatus = registeredStatus;
      });
    };
    fetchCognitoIdAndRegisteredData();

    setLoading(false);
  }, []);

  useEffect(() => {
    // 【非ログイン状態・ログイン状態両方で通るフロー】
    if (isLogin) {
      // ログイン状態の場合
      // すでにpatientテーブルにデータが登録済みの場合は、登録ページを出さない
      if (activeStep === 0 && registeredStatus === 'Revisit') {
        // LINEからカレンダーページへ直接アクセスした場合はカレンダーのstepをセットする。
        if (outPatientIdFromQs != null) {
          setActiveStep(2);
        } else {
          setActiveStep(1);
        }
      } else if (activeStep === 2 && registeredStatus === 'Reserve') {
        // 非ログイン状態からの予約フロー
        // 予約申請 → 新規登録（ユーザ作成・属性登録） → 予約 の場合
        setActiveStep(0);
      } else if (activeStep === 0 && registeredStatus === 'Member') {
        navigate(ReservationUrls.ReservationMemberstatus);
      }
      return;
    }

    // 非ログイン状態の場合
    if (outPatientIdFromQs === null) return navigate('/notFound');
    if (!facilityId) return navigate('/notFound');

    // クエリパラメータにoidとfidがある場合（例：/patient/reservation?fid=0001&oid=010)
    // 予約日時選択ページに遷移する
    setActiveStep(2);
  }, [registeredStatus, outPatientIdFromQs]);

  const [activeStep, setActiveStep] = useState<number>(0);
  const [currentState, setCurrentState] = useState({
    patientInformation: null,
    symptoms: null,
    symptomOthers: null,
    examinationMenuId: outPatientId,
    examinationMenuName: '',
    reservationDateTime: null,
    registeredStatus: registeredStatus,
    facilityId: facilityId,
    facilityName: ''
  });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSetFacilityName = () => {
    const setFacilityName = async () => {
      // currentStateにfacilityNameを格納する
      const facilityList = await executeGet<FacilityResponseType>({
        apiName: 'medirecoWebApi',
        path: '/get-facility-list'
      });
      const facility = facilityList.facilities?.find((facility) => facility.id === facilityId);
      const facilityNameResponse = facility?.facility_name;

      if (facilityNameResponse === undefined) {
        // クエリパラメータにある施設IDが不適切なもの(例:0000)である場合、NotFoundページへ遷移
        navigate('/notFound');
        return;
      }
      setCurrentState((currentState) => ({
        ...currentState,
        facilityName: facilityNameResponse
      }));
    };

    if (facilityId) {
      // クエリパラメータにある施設IDから施設名を取得してセットする
      setFacilityName();
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
        <Grid item xs={12} justifyContent="center" alignItems="center" sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#A08B46' }} />
        </Grid>
      </Grid>
    );
  }

  switch (activeStep) {
    case 0:
      return (
        <Layout title="お客様情報登録" registeredStatus={registeredStatus}>
          <PatientInformationForm
            handleNext={handleNext}
            handleBack={handleBack}
            currentState={currentState}
            setCurrentState={setCurrentState}
            setActiveStep={setActiveStep}
            needsRedirectCalendarPage={outPatientIdFromQs != null}
            toTopPage={() => navigate(ReservationUrls.Top)}
          />
        </Layout>
      );
    case 1:
      return (
        <Layout
          title={currentState.facilityName || '診療メニュー選択'}
          registeredStatus={registeredStatus}
          backButton
          handleBack={() => navigate(-1)}
        >
          <ExaminationMenuForm
            handleNext={handleNext}
            handleBack={handleBack}
            currentState={currentState}
            setCurrentState={setCurrentState}
          />
        </Layout>
      );
    case 2:
      if (isLogin) {
        // ログイン状態の場合
        return (
          <Layout
            title={currentState.facilityName || '予約日時選択'}
            registeredStatus={registeredStatus}
            handleBack={handleBack}
            backButton
          >
            <ReservationDateTimeForm
              isLogin={isLogin}
              handleNext={handleNext}
              handleBack={handleBack}
              currentState={currentState}
              setCurrentState={setCurrentState}
              setActiveStep={setActiveStep}
            />
          </Layout>
        );
      }

      // 非ログイン状態の場合
      return (
        <Layout title={currentState.facilityName || '予約日時選択'} registeredStatus={registeredStatus} noMyPage={true}>
          <ReservationDateTimeForm
            isLogin={isLogin}
            handleNext={handleNext}
            currentState={currentState}
            setCurrentState={setCurrentState}
            setActiveStep={setActiveStep}
          />
        </Layout>
      );
    case 3:
      return (
        <Layout title="お客様情報登録" registeredStatus={registeredStatus} handleBack={handleBack}>
          <PatientInformationForm
            handleNext={handleNext}
            handleBack={handleBack}
            currentState={currentState}
            setCurrentState={setCurrentState}
          />
        </Layout>
      );
    case 4:
      return (
        <Layout title="ご予約完了" registeredStatus={registeredStatus}>
          <ReservationDone currentState={currentState} />
        </Layout>
      );
    default:
      return 'Not found';
  }
}
